import React from 'react';
import type { ReactNode } from 'react';
import type { AstroCommandServices } from '@wix/cli-command-contract';
import { addUserFields } from '@wix/cli-telemetry';
import { BiProvider } from '@wix/cli-telemetry-react';
import { DebugLogProvider } from '@wix/cli-debug-log';
import { getDebugLogFilePath } from '@wix/cli-core-definitions';
import { createPetriOverridesCookie } from '@wix/cli-astro-definitions';
import { createRendererToString } from '@wix/cli-ui-kit';
import { ErrorReporterProvider } from '@wix/cli-error-reporting';
import {
  AuthProvider,
  LoginModeProvider,
  isLoggedInToAccount,
} from '@wix/cli-auth';
import { CliError, CliErrorCode } from '@wix/cli-error';
import { HttpClientProvider } from '@wix/cli-http-client-react';
import { PanoramaProvider } from '@wix/cli-panorama';
import { ProjectModelProvider } from '../model.js';
import type { ProjectModel } from '../model.js';

export async function executeCommand<T>(
  services: AstroCommandServices,
  model: ProjectModel,
  renderFn: (resolve: (data: T) => void) => ReactNode,
  options: { headers?: Record<string, string> } = {}
): Promise<T> {
  const authData = await isLoggedInToAccount();

  if (!authData) {
    throw new CliError({
      code: CliErrorCode.AuthenticationRequired(),
      cause: null,
    });
  }

  services.errorReporter.setUser({ id: authData.userInfo.userId });
  addUserFields(services.biLogger, authData.userInfo.userId);

  const renderer = createRendererToString({ stripAnsi: false });
  const debugLogFilePath = getDebugLogFilePath(model.projectFolder);

  return new Promise((resolve, reject) => {
    renderer.render(
      <BiProvider value={services.biLogger}>
        <ErrorReporterProvider value={services.errorReporter}>
          <PanoramaProvider value={services.panorama}>
            <LoginModeProvider value={services.loginMode}>
              <AuthProvider>
                <HttpClientProvider
                  headers={options.headers}
                  cookies={createPetriOverridesCookie(
                    model.config.httpExperiments
                  )}
                >
                  <DebugLogProvider filePath={debugLogFilePath}>
                    <ProjectModelProvider value={model}>
                      {renderFn((...args) => {
                        renderer.unmount();
                        resolve(...args);
                      })}
                    </ProjectModelProvider>
                  </DebugLogProvider>
                </HttpClientProvider>
              </AuthProvider>
            </LoginModeProvider>
          </PanoramaProvider>
        </ErrorReporterProvider>
      </BiProvider>
    );
    renderer.waitUntilExit().catch(reject);
  });
}
